/**
 * finance moduli uchun umumiy turlar/konstantalar (API + veb + mobil).
 * Faqat finance moduli egasi tahrirlaydi.
 *
 * To'lov (`payout`), pul so'rovi (`moneyRequest`) va fiskal chek (`fiscal`) holatlari — umumiy
 * `statuses.ts` da (StatusPill shu guruhlardan foydalanadi). Bu yerda — moliyaga xos qolgan ro'yxatlar.
 */
import type { L10n } from '../i18n';
import type { Tone } from '../statuses';

export interface FinOption extends L10n {
  tone: Tone;
}

type OptionMap<K extends string> = Record<K, FinOption>;
const opts = <K extends string>(m: OptionMap<K>) => m;

/** Ro'yxatni select/tab variantlariga aylantirish. */
export function finOptions<K extends string>(m: OptionMap<K>): { value: K; label: string; tone: Tone }[] {
  return (Object.keys(m) as K[]).map((value) => ({ value, label: m[value].uz, tone: m[value].tone }));
}

/** Kalit bo'yicha o'zbekcha nom (topilmasa — kalitning o'zi). */
export function finLabel(m: Record<string, FinOption>, key: string | null | undefined): string {
  if (!key) return '—';
  return m[key]?.uz ?? key;
}

// ------------------------------------------------------------------ Hisoblar rejasi
export const ACCOUNT_KINDS = opts({
  seller_payable: { uz: 'Hamkorga qarz', ru: 'Задолженность партнёру', en: 'Payable to partner', zh: '应付合作伙伴', tone: 'violet' },
  courier: { uz: 'Kuryer hisobi', ru: 'Подотчёт курьера', en: 'Courier account', zh: '配送员账户', tone: 'accent' },
  cash_desk: { uz: 'Kassa', ru: 'Касса', en: 'Cash desk', zh: '收银台', tone: 'success' },
  bank: { uz: 'Bank hisobi', ru: 'Расчётный счёт', en: 'Bank account', zh: '银行账户', tone: 'info' },
  clearing: { uz: 'Tranzit (kliring)', ru: 'Транзитный счёт', en: 'In transit (clearing)', zh: '在途（清算）', tone: 'neutral' },
  revenue_fees: { uz: 'Xizmat daromadi', ru: 'Доход от услуг', en: 'Service revenue', zh: '服务收入', tone: 'success' },
  revenue_commission: { uz: 'Komissiya daromadi', ru: 'Комиссионный доход', en: 'Commission revenue', zh: '佣金收入', tone: 'success' },
  other_income: { uz: 'Boshqa daromad', ru: 'Прочий доход', en: 'Other revenue', zh: '其他收入', tone: 'success' },
  expense: { uz: 'Xarajat', ru: 'Расход', en: 'Expense', zh: '支出', tone: 'danger' },
  vat_payable: { uz: 'QQS majburiyati', ru: 'НДС к уплате', en: 'VAT liability', zh: '增值税负债', tone: 'warning' },
});
export type AccountKind = keyof typeof ACCOUNT_KINDS;

/**
 * Kredit tabiatli hisoblar (majburiyat/daromad). Bazada qoldiq "debet +, kredit −" ishorasida
 * saqlanadi; UI da bu hisoblar qoldig'i teskari ishora bilan (musbat) ko'rsatiladi.
 */
export const CREDIT_NORMAL_KINDS: readonly AccountKind[] = [
  'seller_payable',
  'revenue_fees',
  'revenue_commission',
  'other_income',
  'vat_payable',
];
export const displayBalance = (kind: string, balance: number) =>
  (CREDIT_NORMAL_KINDS as readonly string[]).includes(kind) ? -balance : balance;

export const OWNER_TYPES = opts({
  seller: { uz: 'Hamkor', ru: 'Партнёр', en: 'Partner', zh: '合作伙伴', tone: 'violet' },
  courier: { uz: 'Kuryer', ru: 'Курьер', en: 'Courier', zh: '配送员', tone: 'accent' },
  cash_desk: { uz: 'Kassa', ru: 'Касса', en: 'Cash desk', zh: '收银台', tone: 'success' },
  platform: { uz: 'Platforma', ru: 'Платформа', en: 'Platform', zh: '平台', tone: 'info' },
});
export type OwnerType = keyof typeof OWNER_TYPES;

// ------------------------------------------------------------------ Jurnal
export const JOURNAL_KINDS = opts({
  order_sale: { uz: 'Savdo', ru: 'Продажа', en: 'Sales', zh: '销售', tone: 'success' },
  fulfillment_fee: { uz: 'Yig‘ish-qadoqlash', ru: 'Сборка и упаковка', en: 'Pick and pack', zh: '拣货打包', tone: 'violet' },
  storage_fee: { uz: 'Saqlash', ru: 'Хранение', en: 'Storage', zh: '仓储', tone: 'violet' },
  receiving_fee: { uz: 'Qabul', ru: 'Приёмка', en: 'Receiving', zh: '收货', tone: 'violet' },
  delivery_fee: { uz: 'Yetkazib berish', ru: 'Доставка', en: 'Delivery', zh: '配送', tone: 'violet' },
  commission: { uz: 'Komissiya', ru: 'Комиссия', en: 'Commission', zh: '佣金', tone: 'accent' },
  return: { uz: 'Qaytarish', ru: 'Возврат', en: 'Return', zh: '退货', tone: 'danger' },
  return_fee: { uz: 'Qaytarish xizmati', ru: 'Обработка возврата', en: 'Return handling', zh: '退货处理', tone: 'violet' },
  payout: { uz: 'To‘lov (hamkorga)', ru: 'Выплата', en: 'Payout to partner', zh: '合作伙伴结款', tone: 'info' },
  cash_operation: { uz: 'Kassa operatsiyasi', ru: 'Кассовая операция', en: 'Cash operation', zh: '现金操作', tone: 'neutral' },
  courier_settlement: { uz: 'Kuryer hisob-kitobi', ru: 'Расчёт с курьером', en: 'Courier settlement', zh: '配送员结算', tone: 'accent' },
  adjustment: { uz: 'Tuzatish', ru: 'Корректировка', en: 'Adjustment', zh: '调整', tone: 'warning' },
});
export type JournalKind = keyof typeof JOURNAL_KINDS;

/** Hamkordan ushlanadigan xizmat turlari (hamkor kabinetidagi "to'lovlar tarkibi"). */
export const FEE_KINDS = [
  'fulfillment_fee',
  'delivery_fee',
  'storage_fee',
  'receiving_fee',
  'return_fee',
  'commission',
] as const satisfies readonly JournalKind[];
export type FeeKind = (typeof FEE_KINDS)[number];

// ------------------------------------------------------------------ Kassa
export const CASH_OP_TYPES = opts({
  income: { uz: 'Kirim', ru: 'Приход', en: 'Income', zh: '收入', tone: 'success' },
  expense: { uz: 'Chiqim', ru: 'Расход', en: 'Outgoing', zh: '支出', tone: 'danger' },
});
export type CashOpType = keyof typeof CASH_OP_TYPES;

export const CATEGORY_KINDS = opts({
  income: { uz: 'Kirim', ru: 'Приход', en: 'Income', zh: '收入', tone: 'success' },
  expense: { uz: 'Chiqim', ru: 'Расход', en: 'Outgoing', zh: '支出', tone: 'danger' },
  both: { uz: 'Kirim va chiqim', ru: 'Приход и расход', en: 'Income and outgoing', zh: '收支', tone: 'neutral' },
});
export type CategoryKind = keyof typeof CATEGORY_KINDS;

/** Kunlik kassa solishtirishida ruxsat etilgan farq (so'm). */
export const RECONCILE_TOLERANCE = 1000;

// ------------------------------------------------------------------ Moliya hisoboti
export const REPORT_MODES = opts({
  daily: { uz: 'Har kuni', ru: 'Ежедневно', en: 'Daily', zh: '每天', tone: 'info' },
  on_anomaly: { uz: 'Faqat anomaliya bo‘lsa', ru: 'Только при аномалиях', en: 'Only on anomalies', zh: '仅异常时', tone: 'warning' },
  weekly: { uz: 'Haftalik', ru: 'Еженедельно', en: 'Weekly', zh: '每周', tone: 'accent' },
  monthly: { uz: 'Oylik', ru: 'Ежемесячно', en: 'Monthly', zh: '每月', tone: 'violet' },
});
export type ReportMode = keyof typeof REPORT_MODES;

export const ANOMALY_TYPES = opts({
  category_dev: { uz: 'Odatdagidan og‘ish', ru: 'Отклонение от нормы', en: 'Deviation from the norm', zh: '偏离常规', tone: 'warning' },
  new_category: { uz: 'Yangi xarajat turi', ru: 'Новая категория', en: 'New expense type', zh: '新支出类型', tone: 'accent' },
  reconcile: { uz: 'Kassa farqi', ru: 'Расхождение с кассой', en: 'Cash discrepancy', zh: '现金差异', tone: 'danger' },
  no_manual: { uz: 'Kassa fakti kiritilmagan', ru: 'Нет фактической кассы', en: 'Cash count not entered', zh: '未录入现金实点', tone: 'neutral' },
});
export type AnomalyType = keyof typeof ANOMALY_TYPES;
export type AnomalySeverity = 'high' | 'medium' | 'info';

export const WEEKDAYS_SHORT = ['Ya', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh'] as const;

// ------------------------------------------------------------------ Fiskalizatsiya
export const FISCAL_MODES = opts({
  disabled: { uz: 'O‘chirilgan', ru: 'Отключено', en: 'Disabled', zh: '已关闭', tone: 'neutral' },
  test: { uz: 'Test rejimi', ru: 'Тестовый режим', en: 'Test mode', zh: '测试模式', tone: 'warning' },
  production: { uz: 'Ishchi rejim', ru: 'Боевой режим', en: 'Live mode', zh: '正式模式', tone: 'success' },
});
export type FiscalMode = keyof typeof FISCAL_MODES;

export const AUTO_FISCALIZE = opts({
  on_delivery: { uz: 'Yetkazilganda avtomatik', ru: 'Автоматически при доставке', en: 'Automatically on delivery', zh: '送达时自动开票', tone: 'success' },
  on_payment: { uz: 'To‘lov kelganda', ru: 'При оплате', en: 'On payment received', zh: '收款时开票', tone: 'info' },
  manual: { uz: 'Qo‘lda', ru: 'Вручную', en: 'Manual', zh: '手动', tone: 'neutral' },
});
export type AutoFiscalize = keyof typeof AUTO_FISCALIZE;

export const FISCAL_MODULE_KINDS = opts({
  virtual: { uz: 'Virtual FM', ru: 'Виртуальный ФМ', en: 'Virtual fiscal module', zh: '虚拟税控模块', tone: 'accent' },
  device: { uz: 'Qurilma (OKK)', ru: 'Устройство (ККМ)', en: 'Device (cash register)', zh: '税控设备', tone: 'info' },
});
export type FiscalModuleKind = keyof typeof FISCAL_MODULE_KINDS;

export const FISCAL_ASSIGNED_TYPES = opts({
  courier: { uz: 'Kuryer', ru: 'Курьер', en: 'Courier', zh: '配送员', tone: 'accent' },
  cash_desk: { uz: 'Kassa', ru: 'Касса', en: 'Cash desk', zh: '收银台', tone: 'success' },
  branch: { uz: 'Filial', ru: 'Филиал', en: 'Branch', zh: '分支机构', tone: 'info' },
  online: { uz: 'Onlayn savdo', ru: 'Онлайн-продажи', en: 'Online sales', zh: '线上销售', tone: 'violet' },
});
export type FiscalAssignedType = keyof typeof FISCAL_ASSIGNED_TYPES;

export const FISCAL_MODULE_STATUSES = opts({
  active: { uz: 'Faol', ru: 'Активен', en: 'Active', zh: '启用', tone: 'success' },
  blocked: { uz: 'Bloklangan', ru: 'Заблокирован', en: 'Blocked', zh: '已封禁', tone: 'danger' },
  expired: { uz: 'Muddati o‘tgan', ru: 'Истёк', en: 'Expired', zh: '已过期', tone: 'warning' },
});
export type FiscalModuleStatus = keyof typeof FISCAL_MODULE_STATUSES;

export const RECEIPT_KINDS = opts({
  sale: { uz: 'Sotuv', ru: 'Продажа', en: 'Sale', zh: '销售', tone: 'success' },
  refund: { uz: 'Qaytarish', ru: 'Возврат', en: 'Return', zh: '退货', tone: 'danger' },
});
export type ReceiptKind = keyof typeof RECEIPT_KINDS;

/** Fiskal provayder sozlama maydoni (UI forma provayder `describe()` dan quriladi). */
export interface FiscalProviderField {
  key: string;
  label: string;
  type: 'text' | 'url' | 'textarea' | 'select' | 'number';
  /** Sir (API kalit, shaxsiy kalit) — faqat yoziladi, hech qachon qaytarilmaydi. */
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  options?: { value: string; label: string }[];
}

export interface FiscalProviderInfo {
  code: string;
  title: string;
  description: string;
  fields: FiscalProviderField[];
  supportsRefund: boolean;
  /** Tashqi API chaqiradimi (KTS_INTEGRATIONS_LIVE=1 talab qilinadi). */
  external: boolean;
}

/** Fiskal chek qatori (OFD ga yaqin; pul — tiyinda, miqdor ×1000). */
export interface FiscalLine {
  name: string;
  barcode: string | null;
  /** IKPU / MXIK (tasnif.soliq.uz) — OFD da `SPIC`. */
  spic: string | null;
  packageCode: string | null;
  /** O'lchov birligi kodi (dona = 1). */
  units: number;
  /** Miqdor (dona) — ko'rsatish uchun. */
  qty: number;
  /** Qator summasi chegirmagacha, tiyin (narx × miqdor × 100). */
  price: number;
  /** Chegirma, tiyin. */
  discount: number;
  /** QQS summasi (narx ichida), tiyin. */
  vat: number;
  vatPercent: number;
  /** Miqdor × 1000 (OFD `Amount`). */
  amount: number;
  /** Komissioner (Keytosell) hamkor nomidan sotganda — hamkor STIR/JShShIR. */
  commissionInfo: { tin: string | null; pinfl: string | null } | null;
}

export const toTiyin = (sum: number) => Math.round(sum * 100);
export const fromTiyin = (t: number) => Math.round(t) / 100;
